import React, {useState} from "react";
import {
  useTitansRealtime,
  useSessionsRealtime,
  useUsersRealtime,
} from "../../hooks/useFirestore";
import {useAuth} from "../../hooks/useAuth";
import {
  getDisplayDate,
  getDisplayTime,
  convertToDate,
} from "../../utils/dateUtils";
import "../../styles/env.css";
import TitanCard from "../../components/TitanCard";
import FilterableList from "../../components/FilterableList";
import {BaseFilters} from "../../components/ListFilters";

interface Session {
  id: string;
  name: string;
  date: string;
  time: string;
  location: string;
  status: string;
  sessionData?: any;
  isSignedUp?: boolean;
}

// Titan filters interface extending base filters
interface TitanFilters extends BaseFilters {
  status: "all" | "active" | "inactive";
}

const Titans: React.FC = () => {
  const {
    data: titans,
    loading: titansLoading,
    error: titansError,
  } = useTitansRealtime();
  const {
    data: sessions,
    loading: sessionsLoading,
    error: sessionsError,
  } = useSessionsRealtime();
  const {
    data: users,
    loading: usersLoading,
    error: usersError,
  } = useUsersRealtime();
  const {user} = useAuth();

  const [expandedTitanId, setExpandedTitanId] = useState<string | null>(null);

  // Titan filters state
  const [filters, setFilters] = useState<TitanFilters>({
    search: "",
    status: "all",
  });

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      search: "",
      status: "all",
    });
  };

  // Helper function to get DM name by ID
  const getDMName = (dmId: string): string => {
    if (!users || users.length === 0) {
      return "Unknown DM";
    }
    const dm = users.find((u) => u.uid === dmId);
    return dm ? dm.displayName : "Unknown DM";
  };

  // Helper: is user signed up for a session?
  const isSignedUp = (session: any): boolean => {
    if (!user?.uid) return false;
    return (
      session.players &&
      session.players.some((p: any) => p.playerId === user.uid)
    );
  };

  // Helper function to get upcoming sessions for a titan
  const getUpcomingSessionsForTitan = (titanId: string): Session[] => {
    if (!sessions || sessions.length === 0) {
      return [];
    }

    const now = new Date();
    return sessions
      .filter((session) => {
        const sessionDate = convertToDate(session.date);
        return (
          session.titanId === titanId &&
          (session.status === "available" || session.status === "upcoming") &&
          sessionDate > now
        );
      })
      .map((session) => ({
        id: session.id || "",
        name: session.name,
        date: getDisplayDate(session.date),
        time: getDisplayTime(session.date),
        location: session.location,
        status: session.status,
        sessionData: session,
        isSignedUp: isSignedUp(session),
      }));
  };

  // Filter titans based on filters
  const filteredTitans = titans.filter((titan) => {
    // Status filter
    if (filters.status === "active" && !titan.isActive) return false;
    if (filters.status === "inactive" && titan.isActive) return false;

    return true;
  });

  // Show loading state
  if (titansLoading || sessionsLoading || usersLoading) {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Titans</h2>
        <div style={{textAlign: "center", padding: "2rem"}}>
          Loading titans...
        </div>
      </div>
    );
  }

  // Show error state
  if (titansError || sessionsError || usersError) {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Titans</h2>
        <div style={{textAlign: "center", padding: "2rem", color: "red"}}>
          Error loading titans: {titansError || sessionsError || usersError}
        </div>
      </div>
    );
  }

  // Render titans with filtering
  return (
    <div className="env-page-container">
      <h2 className="env-page-title">Titans</h2>
      <section className="env-section" style={{height: "calc(100vh - 200px)"}}>
        <FilterableList
          data={filteredTitans}
          loading={titansLoading}
          error={titansError}
          filters={filters}
          onFiltersChange={setFilters}
          onClearFilters={clearFilters}
          searchFields={["name", "description"]}
          searchPlaceholder="Search titans by name or description..."
          emptyMessage="No titans found matching your criteria"
          additionalFilter={() => true}
          renderItem={(titan) => (
            <TitanCard
              key={titan.id}
              id={titan.id || ""}
              name={titan.name}
              description={titan.description}
              dmName={getDMName(titan.dmId)}
              upcomingSessions={getUpcomingSessionsForTitan(titan.id || "")}
              isOpen={expandedTitanId === titan.id}
              onToggleOpen={() =>
                setExpandedTitanId(
                  expandedTitanId === titan.id ? null : titan.id || null
                )
              }
            />
          )}
          customActiveFilters={
            <>
              {filters.status !== "all" && (
                <span
                  style={{
                    backgroundColor: "var(--sub)",
                    padding: "4px 8px",
                    borderRadius: 4,
                    fontSize: "0.75rem",
                  }}
                >
                  Status: {filters.status}
                </span>
              )}
            </>
          }
        >
          {/* Advanced filters */}
          <div
            style={{
              display: "flex",
              gap: "16px",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {/* Status Filter */}
            <div>
              <label style={{marginRight: "8px", fontSize: "14px"}}>
                Status:
              </label>
              <select
                value={filters.status}
                onChange={(e) =>
                  setFilters({...filters, status: e.target.value as any})
                }
                style={{
                  padding: "4px 8px",
                  borderRadius: 4,
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
              >
                <option value="all">All Titans</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </FilterableList>
      </section>
    </div>
  );
};

export default Titans;
